import Spinner from "../../components/Spinner";
import Button from "../../components/Button";
import styles from "./SelectOptions.module.scss";
import cx from "classnames";
import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { SelectOption } from "./SelectOption";
import { Text } from "../../components/Text";
import { SrOnly } from "../../components";
import Divider from "../../components/Divider";
import { useMergeRefs } from "../../hooks/useMergeRefs";
import {
  useOptionsSections,
  flattenSectionsToVirtualRows,
  VirtualRow,
} from "../hooks/useOptionsSections";
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import type { Range } from "@tanstack/react-virtual";
import { OptionsPanelProps, OptionsPanelHandle } from "./OptionsPanel";

const EST_PADDING_BLOCK = 16; // $size-2 * 2
const EST_LINE_HEIGHT = 18; // $size-4 + $size-half
const EST_CHIP_ROW_HEIGHT = 28; // $size-1 margin + ~24px chip
const EST_SIDE_CONTENT_HEIGHT = 40; // $size-10
const EST_HEADER_HEIGHT = 32;
const EST_DIVIDER_HEIGHT = 17;
const EST_LOAD_MORE_HEIGHT = 40;
const EST_BASE_ITEM_HEIGHT = EST_PADDING_BLOCK + EST_LINE_HEIGHT; // 34

function estimateRowHeight(row: VirtualRow): number {
  if (row.type === "header") return EST_HEADER_HEIGHT;
  if (row.type === "divider") return EST_DIVIDER_HEIGHT;
  if (row.type === "load-more") return EST_LOAD_MORE_HEIGHT;

  const content = row.item.original.content;
  if (!content) return EST_BASE_ITEM_HEIGHT;

  let contentHeight = EST_LINE_HEIGHT;
  if (content.description) contentHeight += EST_LINE_HEIGHT;
  if (content.chips?.length) contentHeight += EST_CHIP_ROW_HEIGHT;
  if (content.avatar || content.icon)
    contentHeight = Math.max(contentHeight, EST_SIDE_CONTENT_HEIGHT);

  return EST_PADDING_BLOCK + contentHeight;
}

export const VirtualizedOptionsPanel = forwardRef<
  OptionsPanelHandle,
  OptionsPanelProps
>(
  (
    {
      isOpen,
      items,
      getMenuProps,
      getItemProps,
      highlightedIndex,
      getCheckedState,
      selectionType,
      hasMore,
      onLoadMore,
      loading,
      loadingMore,
      disableAutoLoadMore,
      disabled,
      className,
      style,
      sectionsMeta = [],
    },
    ref,
  ) => {
    const baseId = useId();
    const scrollSentinelRef = useRef<HTMLDivElement>(null);
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
      if (!isOpen) {
        setIsScrolled(false);
        return;
      }
      if (!scrollSentinelRef.current) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setIsScrolled(!entry.isIntersecting);
          });
        },
        { threshold: 0, root: scrollerRef.current },
      );
      observer.observe(scrollSentinelRef.current);
      return () => observer.disconnect();
    }, [isOpen]);

    const anyPinnedLoading = sectionsMeta.some(
      (s) => s.type === "pinned" && s.loading,
    );

    const sections = useOptionsSections(items, sectionsMeta);

    const { virtualRows, downshiftIndexToVirtualRowIndex, stickyIndices } =
      useMemo(
        () => flattenSectionsToVirtualRows(sections, hasMore),
        [sections, hasMore],
      );

    const stickyHeightRef = useRef(EST_BASE_ITEM_HEIGHT);

    const rangeExtractor = useCallback(
      (range: Range) => {
        if (stickyIndices.length === 0) {
          return defaultRangeExtractor(range);
        }
        const activeStickyIndex =
          [...stickyIndices]
            .reverse()
            .find((index) => range.startIndex >= index) ?? 0;

        const next = new Set([
          activeStickyIndex,
          ...defaultRangeExtractor(range),
        ]);

        return [...next].sort((a, b) => a - b);
      },
      [stickyIndices],
    );

    const virtualizer = useVirtualizer({
      count: virtualRows.length,
      getScrollElement: () => scrollerRef.current,
      estimateSize: (index) => estimateRowHeight(virtualRows[index]),
      overscan: 5,
      rangeExtractor,
      scrollPaddingStart:
        stickyIndices.length > 0 ? stickyHeightRef.current : 0,
      enabled: isOpen,
      getItemKey: (index) => {
        const row = virtualRows[index];
        if (row.type === "item") return row.item.id;
        if (row.type === "header") return `header-${row.sectionIndex}`;
        if (row.type === "divider") return `divider-${row.sectionIndex}`;
        return "load-more";
      },
    });

    const virtualItems = virtualizer.getVirtualItems();

    if (stickyIndices.length > 0) {
      const stickyItem = virtualItems.find((vi) =>
        stickyIndices.includes(vi.index),
      );
      if (stickyItem) {
        stickyHeightRef.current = stickyItem.size;
      }
    }

    const downshiftMapRef = useRef(downshiftIndexToVirtualRowIndex);
    downshiftMapRef.current = downshiftIndexToVirtualRowIndex;

    const virtualizerRef = useRef(virtualizer);
    virtualizerRef.current = virtualizer;

    useImperativeHandle(ref, () => ({
      scrollToTop: () => {
        if (virtualizerRef.current) {
          virtualizerRef.current.scrollToOffset(0);
        }
      },
      scrollToIndex: (index: number) => {
        if (index <= 0) {
          // special case for first item allows group header to be visible
          virtualizerRef.current.scrollToOffset(0);
          return;
        }
        const virtualRowIndex = downshiftMapRef.current[index];
        if (virtualRowIndex != null) {
          virtualizerRef.current.scrollToIndex(virtualRowIndex, {
            align: "auto",
          });
        }
      },
    }));

    const onLoadMoreRef = useRef(onLoadMore);
    onLoadMoreRef.current = onLoadMore;

    // Trigger load more when last item is visible
    useEffect(() => {
      if (!hasMore || loadingMore || disableAutoLoadMore) return;
      const lastItem = virtualItems[virtualItems.length - 1];
      if (!lastItem) return;
      if (lastItem.index >= virtualRows.length - 1) {
        onLoadMoreRef.current();
      }
    }, [
      hasMore,
      loadingMore,
      disableAutoLoadMore,
      virtualItems,
      virtualRows.length,
    ]);

    const isSticky = useCallback(
      (index: number) => stickyIndices.includes(index),
      [stickyIndices],
    );

    const renderVirtualRow = (row: VirtualRow): ReactNode => {
      if (row.type === "item") {
        return (
          <SelectOption
            item={row.item}
            index={row.index}
            getItemProps={getItemProps}
            selectionType={selectionType}
            checked={getCheckedState(row.item)}
            highlighted={highlightedIndex === row.index}
            disabled={(disabled || row.item.disabled) ?? false}
            isListScrolled={isScrolled}
          />
        );
      }

      if (row.type === "header") {
        const labelId = `${baseId}-section-${row.sectionIndex}`;
        return (
          <div
            role="presentation"
            aria-hidden="true"
            className={styles["pinned-section-header"]}
          >
            <span id={labelId}>
              <Text variant="eyebrow">{row.label}</Text>
            </span>
          </div>
        );
      }

      if (row.type === "divider") {
        return (
          // Decorative — group boundaries are already conveyed by role="group" with aria-labelledby
          <div
            role="presentation"
            aria-hidden="true"
            className={styles["divider"]}
          >
            <Divider />
          </div>
        );
      }

      if (row.type === "load-more") {
        if (loadingMore) {
          return (
            <div role="presentation" className={styles["load-more"]}>
              <Spinner size="small" />
              <SrOnly>Loading more...</SrOnly>
            </div>
          );
        }
        return (
          <div role="presentation">
            <div className={styles["load-more"]}>
              <Button
                appearance="secondary"
                size="small"
                onClick={() => onLoadMoreRef.current()}
              >
                Load more
              </Button>
            </div>
          </div>
        );
      }

      return null;
    };

    const menuProps = getMenuProps({
      className: cx(styles["scroller"], className),
      style,
    });

    const menuRef = useMergeRefs([menuProps.ref, scrollerRef]);

    return (
      <div {...menuProps} ref={menuRef}>
        <div className={styles["select-field-list"]}>
          {isOpen && (
            <div
              ref={scrollSentinelRef}
              className={styles["scroll-sentinel"]}
              role="presentation"
              aria-hidden="true"
            />
          )}
          {isOpen && (
            <>
              {loading || anyPinnedLoading ? (
                <div role="presentation" className={styles["empty"]}>
                  <Spinner size="medium" />
                </div>
              ) : items.length > 0 ? (
                <div
                  role="presentation"
                  aria-hidden="true"
                  style={{
                    height: virtualizer.getTotalSize(),
                    width: "100%",
                    position: "relative",
                  }}
                >
                  {virtualItems.map((virtualItem) => {
                    const row = virtualRows[virtualItem.index];
                    const sticky = isSticky(virtualItem.index);

                    return (
                      <div
                        key={virtualItem.key}
                        data-index={virtualItem.index}
                        ref={virtualizer.measureElement}
                        style={
                          sticky
                            ? {
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                              }
                            : {
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                transform: `translateY(${virtualItem.start}px)`,
                              }
                        }
                      >
                        {renderVirtualRow(row)}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div role="presentation" className={styles["empty"]}>
                  <Text subdued size="small">
                    No match found
                  </Text>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  },
);

VirtualizedOptionsPanel.displayName = "VirtualizedOptionsPanel";
